import { useEffect, useState, useCallback } from "react";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { notifyPdfNoStamp } from "@/lib/printNotice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Tags, Printer, FileText, RefreshCw, Plus, Ban, Archive, Loader2,
  CheckCircle2, AlertTriangle, Link2, Wifi, WifiOff, Ruler, Search, ChevronDown, ChevronRight,
} from "lucide-react";
import { printZpl, printZplBatch, printZplChunked, checkBrowserPrint, PrintAborted, ZEBRA_FLUSH_HINT } from "@/lib/zebra";
import { getPrintChunkSize, setPrintChunkSize, usePrintChunkSize } from "@/lib/printSettings";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { usePrintJob } from "@/lib/usePrintJob";
import { usePrintConfirm } from "@/lib/usePrintConfirm";
import { beginPrintActive, endPrintActive } from "@/lib/printActive";
import { PrintStopBanner } from "@/components/PrintStopBanner";
import { LabelsTodayBadge } from "@/components/LabelsTodayBadge";
import { ReprintReasonPicker } from "@/components/ReprintReasonPicker";
import { mediaLabelOf } from "@/lib/tagMedia";
import { MediaPicker } from "@/components/MediaPicker";
import { printerDisplayName } from "@/lib/printerMap";
import { ScanTextInput } from "@/components/ScanTextInput";
import { SearchCameraButton } from "@/components/search/engine";

const STATUS_FILTERS = ["", "generated", "printed", "attached", "reprinted", "retired", "voided"];

function Stat({ testid, label, value, accent }) {
  return (
    <div data-testid={testid} className="border border-slate-200 bg-white p-4">
      <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`font-display text-3xl font-black mt-1 ${accent ? "text-[#B01E28]" : "text-[#1F1F1F]"}`}>{value}</div>
    </div>
  );
}

function StatusPill({ s }) {
  const map = {
    generated: "bg-slate-100 text-slate-700", printed: "bg-blue-100 text-blue-700",
    attached: "bg-green-100 text-green-700", reprinted: "bg-amber-100 text-amber-700",
    retired: "bg-slate-200 text-slate-500", voided: "bg-red-100 text-red-700",
  };
  return <span className={`font-mono text-[10px] uppercase px-1.5 py-0.5 ${map[s] || "bg-slate-100"}`}>{s}</span>;
}

function PrintStatusPill({ s }) {
  const map = {
    unprinted: "bg-slate-100 text-slate-500", printing: "bg-blue-100 text-blue-700 animate-pulse",
    printed: "bg-green-100 text-green-700", partial: "bg-amber-100 text-amber-800",
    interrupted: "bg-red-100 text-red-700",
  };
  const label = { interrupted: "interrupted", partial: "partial", printing: "printing", printed: "printed", unprinted: "not printed" }[s] || s;
  return <span data-testid={`print-status-${s}`} className={`font-mono text-[10px] uppercase px-1.5 py-0.5 rounded ${map[s] || "bg-slate-100"}`}>{label}</span>;
}

export default function MakeTagsPage() {
  const [stats, setStats] = useState(null);
  const [batches, setBatches] = useState([]);          // the runs with paper outstanding
  const [batchMeta, setBatchMeta] = useState(null);    // {total, shown, has_more, counts}
  const [doneRuns, setDoneRuns] = useState(null);      // the printed pile: null until he expands it
  const [doneMeta, setDoneMeta] = useState(null);
  const [runQ, setRunQ] = useState("");                // search every run: number, date, or a tag in it
  const [showDone, setShowDone] = useState(false);
  const [tags, setTags] = useState([]);
  const [tagTotal, setTagTotal] = useState(0);
  const [count, setCount] = useState(24);
  const [busy, setBusy] = useState(false);
  const [printingBatch, setPrintingBatch] = useState(null);
  const [batchProgress, setBatchProgress] = useState(null);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  const [zebra, setZebra] = useState({ ok: null });
  const [media, setMedia] = useState("hang_tag");   // print-time media (any TAG_MEDIA)
  const mediaLabel = mediaLabelOf(media);
  const printJob = usePrintJob();
  const { printConfirmDialog, confirmAndRun } = usePrintConfirm();
  const [showCalResult, setShowCalResult] = useState(false);
  const [safeSize] = usePrintChunkSize();
  const [rangeFrom, setRangeFrom] = useState("");
  const [rangeTo, setRangeTo] = useState("");
  const [rangePreview, setRangePreview] = useState(null);
  const [rangeBusy, setRangeBusy] = useState(false);
  const [recover, setRecover] = useState(null);   // batch pending recovery (shows numbers + drop time)
  const [reasonPicker, setReasonPicker] = useState(null);   // { count, onPick(reason,text) } | null

  // HIS ORDER, 2026-10-14: the runs with paper still outstanding on top; the printed pile folded
  // into one line he opens when he wants it. Both come from the server, paged and searchable, so
  // there is no run past which a run stops existing.
  const refresh = useCallback(async (page = 1) => {
    try {
      const params = { paged: true, page, page_size: 25, q: runQ.trim() };
      const [s, b] = await Promise.all([
        api.get("/tags/stats"),
        api.get("/tags/batches", { params: { ...params, status: runQ.trim() ? "" : "needs_attention" } }),
      ]);
      setStats(s.data);
      setBatches((prev) => (page === 1 ? (b.data.items || []) : [...(prev || []), ...(b.data.items || [])]));
      setBatchMeta(b.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [runQ]);

  const loadDone = useCallback(async (page = 1) => {
    try {
      const { data } = await api.get("/tags/batches", {
        params: { paged: true, page, page_size: 25, status: "printed", q: runQ.trim() },
      });
      setDoneRuns((prev) => (page === 1 ? (data.items || []) : [...(prev || []), ...(data.items || [])]));
      setDoneMeta(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [runQ]);

  const loadTags = useCallback(async () => {
    try {
      const params = {};
      if (filter) params.status = filter;
      if (q) params.q = q;
      const { data } = await api.get("/tags", { params });
      setTags(data.tags); setTagTotal(data.total);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [filter, q]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { loadTags(); }, [loadTags]);
  useEffect(() => { checkBrowserPrint().then(setZebra); }, []);

  const reconnect = async () => { setZebra({ ok: null }); setZebra(await checkBrowserPrint()); };

  const generate = async () => {
    const n = parseInt(count, 10);
    if (!n || n < 1) { toast.error("Enter how many tags to generate."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/tags/generate", { count: n });
      toast.success(`Generated ${data.count} tags: ${data.first_seq} → ${data.last_seq} (${data.batch_id})`);
      await refresh(); await loadTags();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const sendZpl = async (zpl, label, mediaType = media) => {
    try {
      const dev = await printZpl(zpl, null, mediaType);
      toast.success(`Sent ${label} to ${printerDisplayName(dev)}.`);
      return true;
    } catch (e) {
      toast.error(`Zebra Browser Print not reachable. Use the PDF fallback instead. (${e.message})`);
      return false;
    }
  };

  const printBatch = async (b) => {
    if (printingBatch) return;   // a batch is already running → block overlap (root-cause fix)
    const { batch_id, count } = b;
    confirmAndRun({ title: `Print run ${batch_id}`, labelCount: count, copies: 1, alreadyPrinted: b.printed_count || 0, noun: `${mediaLabel} tags`, mediaType: media }, async () => {
      setPrintingBatch(batch_id);
      setBatchProgress({ done: 0, total: count });
      try {
        const { data } = await api.post(`/tags/print/${batch_id}`, null, { params: { media } });
        const runId = data.run_id;
        const list = (data.tags || []).map((t) => t.zpl).filter(Boolean);
        if (!list.length) { toast.error("No tags to print in this batch."); return; }
        const tid = toast.loading(`Printing 0/${list.length} tags (${batch_id})…`);
        const signal = printJob.start(`Print run ${batch_id} · ${mediaLabel}`, list.length);
        // Keep the app alive through a background 401 while printing (bounded — see printActive.js).
        beginPrintActive();
        let lastReported = 0;
        const reportSent = (sent, done) =>
          api.post(`/tags/print/${batch_id}/confirm`, { run_id: runId, sent_count: sent, media, done }).catch(() => {});
        try {
          const dev = await printZplChunked(list, { chunkSize: getPrintChunkSize(), onProgress: (done, total) => {
            setBatchProgress({ done, total });
            printJob.progress(done, total);
            toast.loading(`Printing ${done}/${total} tags (${batch_id})…`, { id: tid });
            // Throttled truthful checkpoint so a mid-run drop still records what got sent.
            if (done - lastReported >= 20) { lastReported = done; reportSent(done, false); }
          }, signal, mediaType: media });
          await reportSent(list.length, true);   // terminal: everything sent
          toast.success(`Done — sent ${list.length} ${mediaLabel} to ${printerDisplayName(dev)}.`, { id: tid });
        } catch (e) {
          const sent = e.done ?? lastReported ?? 0;
          await reportSent(sent, true);           // terminal: only what actually went out
          if (e instanceof PrintAborted || e.aborted) {
            toast.error(`Stopped after ${sent} of ${e.total ?? list.length} tags — the rest stay unprinted for recovery. ${ZEBRA_FLUSH_HINT}`, { id: tid, duration: 12000 });
          } else {
            toast.error(`Printer stopped mid-batch — ${sent} sent, the rest are recoverable below. (${e.message})`, { id: tid });
          }
        } finally {
          endPrintActive();
          printJob.end();
        }
        await refresh(); await loadTags();
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
      finally { setPrintingBatch(null); setBatchProgress(null); }
    });
  };

  // Reusable Browser-Print runner for an explicit list of tag numbers (range +
  // batch recovery). Confirms exactly which numbers were SENT so status stays honest.
  const runRangePrint = async (seqs, label, reason = "batch_recovery", reasonText = "", printMedia = media) => {
    const { data } = await api.post(`/tags/print-range`, { seqs, media: printMedia, reason, reason_text: reasonText });
    const items = data.tags || [];
    const list = items.map((t) => t.zpl).filter(Boolean);
    const sentSeqs = items.map((t) => t.seq_id);
    const pmLabel = mediaLabelOf(printMedia);
    if (!list.length) { toast.error("Nothing printable in that selection."); return; }
    const tid = toast.loading(`Printing 0/${list.length} (${label})…`);
    const signal = printJob.start(`${label} · ${pmLabel}`, list.length);
    beginPrintActive();
    try {
      const dev = await printZplChunked(list, { chunkSize: getPrintChunkSize(), onProgress: (d, t) => {
        printJob.progress(d, t); toast.loading(`Printing ${d}/${t} (${label})…`, { id: tid });
      }, signal, mediaType: printMedia });
      await api.post(`/tags/print-range/confirm`, { sent_seqs: sentSeqs, media: printMedia }).catch(() => {});
      toast.success(`Done — sent ${list.length} ${pmLabel} to ${printerDisplayName(dev)}.`, { id: tid });
    } catch (e) {
      const sent = e.done ?? 0;
      await api.post(`/tags/print-range/confirm`, { sent_seqs: sentSeqs.slice(0, sent), media: printMedia }).catch(() => {});
      toast.error(`Stopped after ${sent} of ${list.length}. ${ZEBRA_FLUSH_HINT}`, { id: tid, duration: 12000 });
    } finally { endPrintActive(); printJob.end(); await refresh(); await loadTags(); }
  };

  const previewRange = async () => {
    if (!rangeFrom.trim() || !rangeTo.trim()) { toast.error("Enter both a start and end FAS number."); return; }
    setRangeBusy(true); setRangePreview(null);
    try {
      const { data } = await api.post(`/tags/print-range/preview`, { from_seq: rangeFrom.trim(), to_seq: rangeTo.trim(), media });
      setRangePreview(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRangeBusy(false); }
  };

  // safeOnly=true → only not-yet-printed-on-this-media (the safe default). false → everything
  // printable in the range (deliberate reprint of already-printed / bound labels).
  const printRange = (safeOnly) => {
    if (!rangePreview) return;
    const pick = rangePreview.items.filter((i) => !i.voided && (safeOnly ? !i.already_this_media : true));
    if (!pick.length) { toast.error("Nothing to print with this choice."); return; }
    const seqs = pick.map((i) => i.seq_id);
    const reprint = pick.filter((i) => i.already_this_media).length;
    const bound = pick.filter((i) => i.bound).length;
    const label = `Range ${rangePreview.from}–${rangePreview.to}`;
    const run = (reason, reasonText, printMedia = media) => confirmAndRun({
      title: `Print range ${rangePreview.from}–${rangePreview.to}`, labelCount: seqs.length, copies: 1,
      alreadyPrinted: reprint, noun: `${mediaLabelOf(printMedia)} tags`, mediaType: printMedia,
      note: reprint
        ? `Prints ${seqs.length} tag(s) as ${mediaLabelOf(printMedia)} — includes ${reprint} already printed${bound ? ` and ${bound} bound to a part` : ""}. Makes DUPLICATE physical labels.`
        : `Prints ${seqs.length} tag(s) as ${mediaLabelOf(printMedia)}.`,
    }, () => runRangePrint(seqs, label, reason, reasonText, printMedia));
    // A deliberate reprint of already-printed labels must carry a reason (audit trail).
    if (reprint > 0) {
      setReasonPicker({ count: reprint, onPick: (reason, text, printMedia) => { setReasonPicker(null); run(reason, text, printMedia); } });
    } else {
      run(null, "", media);   // all-new first prints — no reason needed
    }
  };

  const doRecover = (b) => {
    confirmAndRun({
      title: `Recover run ${b.batch_id}`, labelCount: b.not_printed_count || 0, copies: 1, alreadyPrinted: 0,
      noun: `${mediaLabel} tags`, mediaType: media,
      note: `Reprints only the ${b.not_printed_count} tag(s) that did NOT print in this run.`,
    }, async () => { setRecover(null); await runRangePrint(b.not_printed_seqs || [], `Recover ${b.batch_id}`, "batch_recovery", ""); });
  };


  const testPrint = async () => {
    try {
      const { data } = await api.get(`/tags/test-zpl`, { params: { media } });
      await sendZpl(data.zpl, `test tag (${mediaLabel})`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const printOne = async (t) => {
    // per-media "already printed": only nag if THIS media was already printed for this tag
    // (a fresh 3×1 for a hang-tagged part is NOT a reprint). Matches the backend truth.
    const already = (t.printed_media || []).includes(media);
    confirmAndRun({ title: `Print ${t.seq_id}`, labelCount: 1, copies: 1, alreadyPrinted: already ? 1 : 0, noun: `${mediaLabel} tag`, mediaType: media }, async () => {
      try {
        const { data } = await api.post(`/tags/${t.id}/print`, null, { params: { media } });
        const ok = await sendZpl(data.zpl, `${data.seq_id} (${mediaLabel})`);
        // confirm-driven stamp: only after the browser actually accepted the job
        if (ok) await api.post(`/tags/print-range/confirm`, { sent_seqs: [data.seq_id], media }).catch(() => {});
        await refresh(); await loadTags();
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    });
  };

  // ---- One-time PRINT CALIBRATION LADDER: fire each size as ONE uninterrupted run
  // (deliberately NOT auto-split) so the operator sees which size is the last to fully
  // print — that's the reliable max to set as Safe batch size. Uses throwaway ZPL
  // (no DB tag records, no FAS numbers consumed) — just stock.
  const CAL_STEPS = [80, 120, 150, 175, 200];
  const calZpl = (step, i, n) =>
    `^XA^CI28^CF0,70^FO40,55^FDCAL ${step}^FS^CF0,45^FO40,145^FD${i} / ${n}^FS^FO40,215^GB760,3,3^FS^CF0,28^FO40,235^FDcalibration test - not a real tag^FS^XZ`;
  const runCalibrationLadder = () => {
    const total = CAL_STEPS.reduce((a, b) => a + b, 0);
    confirmAndRun({
      title: "Print calibration ladder",
      labelCount: total, copies: 1, noun: "test labels", hideSplit: true, mediaType: media,
      note: `Prints ${CAL_STEPS.join(", ")} as ${CAL_STEPS.length} back-to-back UNINTERRUPTED runs (${total} labels of stock, NO auto-split). Watch which size is the LAST to fully print — that's your reliable max. Then set it as Safe batch size in any print confirm.`,
    }, async () => {
      const dev = await checkBrowserPrint();
      if (!dev.ok) { toast.error("Zebra Browser Print not reachable — check the printer connection."); return; }
      for (const step of CAL_STEPS) {
        const zpls = Array.from({ length: step }, (_, k) => calZpl(step, k + 1, step));
        const tid = toast.loading(`Calibration ${step}: 0/${step}…`);
        const signal = printJob.start(`Calibration · run of ${step}`, step);
        try {
          await printZplBatch(zpls, (d, t) => { printJob.progress(d, t); toast.loading(`Calibration ${step}: ${d}/${t}…`, { id: tid }); }, 1500, signal, media);
          toast.success(`Ran ${step} — check the printer: did ALL ${step} come out?`, { id: tid, duration: 9000 });
        } catch (e) {
          printJob.end();
          if (e instanceof PrintAborted || e.aborted) { toast.error(`Calibration stopped during the ${step} run. ${ZEBRA_FLUSH_HINT}`, { id: tid, duration: 12000 }); return; }
          toast.error(`Calibration ${step} errored — treat sizes above the last clean one as unsafe. (${e.message})`, { id: tid }); return;
        }
        printJob.end();
        await new Promise((r) => setTimeout(r, 4000));   // pause so operator eyeballs output + printer drains
      }
      toast.success("Ladder done — set your safe size below.", { duration: 6000 });
      setShowCalResult(true);
    });
  };

  const openPdf = async (path) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      toast.message("PDF opened.", { description: "Print at Actual Size / 100% — NOT Fit to Page." });
      notifyPdfNoStamp();
    } catch (e) { toast.error("Could not build PDF."); }
  };

  const lifecycle = async (id, action) => {
    try {
      await api.post(`/tags/${id}/${action}`, { reason: action });
      toast.success(`Tag ${action}.`);
      await refresh(); await loadTags();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const by = stats?.by_status || {};

  return (
    <div data-testid="make-tags-page">
      <PrintStopBanner job={printJob.job} onStop={printJob.stop} />
      {printConfirmDialog}
      <Dialog open={showCalResult} onOpenChange={setShowCalResult}>
        <DialogContent className="rounded-lg border-slate-200 sm:max-w-md" data-testid="cal-result-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Ruler className="w-5 h-5 text-[#B01E28]" />Set your safe batch size</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Tap the <b>largest size that fully printed</b> with no missing labels. That becomes your Safe batch size — runs above it auto-split into sub-runs.</p>
          <div className="flex flex-wrap gap-2 py-2" data-testid="cal-chip-row">
            {CAL_STEPS.map((n) => (
              <button key={n} data-testid={`cal-chip-${n}`}
                onClick={() => { setPrintChunkSize(n); toast.success(`Safe batch size set to ${n} labels.`); setShowCalResult(false); }}
                className={`rounded-full px-5 py-2.5 font-display font-bold text-lg tabular-nums border-2 transition-colors ${safeSize === n ? "border-[#B01E28] bg-red-50 text-[#B01E28]" : "border-slate-200 hover:border-[#B01E28] hover:bg-red-50"}`}>
                {n}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-slate-400">Current: <b>{safeSize}</b> labels. You can change it anytime from any print confirmation.</p>
        </DialogContent>
      </Dialog>
      <h1 className="font-display text-2xl font-bold tracking-tight mb-1 flex items-center gap-2">
        <Tags className="w-6 h-6 text-[#B01E28]" />Parts Tags and Stickers
      </h1>
      <p className="text-sm text-slate-500 mb-4">Generate pre-printed FAS tags in print runs, print them on the Zebra ZT411, and track which are still loose. Tags bind to parts later in Identify &amp; Completion.</p>

      {/* Zebra status */}
      <div data-testid="zebra-status" className={`border p-3 mb-5 flex items-center justify-between text-sm ${zebra.ok ? "border-green-300 bg-green-50" : zebra.ok === false ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"}`}>
        <div className="flex items-center gap-2">
          {zebra.ok ? <Wifi className="w-4 h-4 text-green-600" /> : <WifiOff className="w-4 h-4 text-amber-600" />}
          {zebra.ok === null ? "Checking Zebra Browser Print…" : zebra.ok ? `Zebra Browser Print connected — ${zebra.count} printer${zebra.count === 1 ? "" : "s"} (each label shows its destination when you confirm)` : "Zebra Browser Print not detected. Install/launch it, or use the PDF fallback (print at 100%)."}
        </div>
        <Button data-testid="zebra-reconnect" size="sm" variant="outline" className="rounded-lg h-8" onClick={reconnect}><RefreshCw className="w-3.5 h-3.5 mr-1" />Recheck</Button>
      </div>

      <div className="flex justify-end items-center gap-2 mb-5">
        <Button data-testid="calibrate-ladder-button" variant="outline" size="sm"
          className="rounded-lg border-slate-200 text-slate-600" onClick={runCalibrationLadder}
          title="One-time: print test runs of 80/120/150/175/200 to find your printer's reliable max, then set it as Safe batch size">
          <Ruler className="w-3.5 h-3.5 mr-1.5" />Calibrate print size
        </Button>
        <LabelsTodayBadge />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        <Stat testid="stat-total" label="Total Tags" value={stats?.total ?? "—"} />
        <Stat testid="stat-generated" label="Generated" value={by.generated ?? "—"} />
        <Stat testid="stat-printed-unbound" label="Printed · Unbound" value={stats?.printed_unbound ?? "—"} accent />
        <Stat testid="stat-attached" label="Bound to Parts" value={stats?.bound ?? "—"} />
        <Stat testid="stat-voided" label="Voided / Retired" value={(by.voided ?? 0) + (by.retired ?? 0)} />
      </div>

      {/* Next-tag counter (read-only) — verify the counter before printing a real batch */}
      <div data-testid="next-tag-banner" className={`border rounded-lg p-3 mb-6 flex flex-wrap items-center justify-between gap-2 ${stats?.counter_value === 0 ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white"}`}>
        <div className="flex items-center gap-2 text-sm">
          <Tags className="w-4 h-4 text-[#B01E28]" />
          <span className="text-slate-600">Next tag to be assigned:</span>
          <span data-testid="next-tag-value" className="font-mono font-bold text-base text-[#1F1F1F]">{stats?.next_tag ?? "—"}</span>
        </div>
        {stats?.counter_value === 0
          ? <span data-testid="counter-zero-ok" className="text-xs font-mono text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" />Counter is at zero — ready for the first real batch (FAS-000001).</span>
          : <span className="text-xs font-mono text-slate-400">Counter value: {stats?.counter_value ?? "—"}</span>}
      </div>

      {/* Generate + test print */}
      <div className="border border-slate-200 bg-white p-4 mb-6">
        <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-3">Generate a print run</div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500">How many</label>
            <Input data-testid="generate-count" type="number" min={1} max={500} value={count} onChange={(e) => setCount(e.target.value)} className="rounded-lg border-slate-200 mt-1 w-28 font-mono" />
          </div>
          <Button data-testid="generate-button" onClick={generate} disabled={busy} className="rounded-lg bg-[#E0781A] text-white hover:bg-[#C76714]">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}Generate
          </Button>
          <div className="flex-1" />
          <Button data-testid="test-print-button" onClick={testPrint} variant="outline" className="rounded-lg"><Printer className="w-4 h-4 mr-1" />Test print (ZPL)</Button>
          <Button data-testid="test-pdf-button" onClick={() => openPdf("/tags/test-pdf")} variant="outline" className="rounded-lg"><FileText className="w-4 h-4 mr-1" />Test PDF</Button>
        </div>
      </div>

      {/* Print run history */}
      <div className="border border-slate-200 bg-white p-4 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Print runs — paper still out</div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Print as</span>
            <MediaPicker value={media} onChange={setMedia} />
          </div>
        </div>
        <p className="text-[11px] text-slate-400 mb-3">Same FAS number, same QR + barcode — only the physical label format differs. Media is chosen here at print time; a number should be printed once, on one media.</p>
        {/* SEARCH EVERY RUN — the number, the date, or a tag that was printed on it. */}
        <div className="flex items-center gap-2 mb-3">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="runs-search" value={runQ} onChange={(e) => setRunQ(e.target.value)}
              placeholder="Search runs — run number, a date like 2026-10, or a tag number"
              className="pl-9 h-10 rounded-lg border-slate-200" />
          </div>
          <SearchCameraButton testid="runs-search" onScan={setRunQ} />
          {batchMeta && (
            <span data-testid="runs-showing" className="font-mono text-[11px] text-slate-500">
              {batchMeta.total === 0 ? "no runs match"
                : batchMeta.shown >= batchMeta.total ? `showing all ${batchMeta.total}`
                  : `showing ${batchMeta.shown} of ${batchMeta.total}`}
              {runQ.trim() ? "" : ` with paper out · ${batchMeta.counts?.printed ?? 0} done`}
            </span>
          )}
        </div>
        {batches.length === 0 ? (
          <p data-testid="runs-none-outstanding" className="text-sm text-slate-400">
            {runQ.trim() ? `No run matches “${runQ}”.` : "Every run is printed — nothing waiting on paper."}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="batch-table">
              <thead><tr className="text-left font-mono text-[10px] uppercase text-slate-400 border-b border-slate-200">
                <th className="py-2 pr-3">Print run</th><th className="pr-3">Range</th><th className="pr-3">Count</th><th className="pr-3">Printed</th><th className="pr-3">Status</th><th className="pr-3">Bound</th><th className="pr-3">Created</th><th className="text-right">Print</th>
              </tr></thead>
              <tbody>
                {batches.map((b) => (
                  <tr key={b.batch_id} data-testid={`batch-row-${b.batch_id}`} className="border-b border-slate-100">
                    <td className="py-2 pr-3 font-mono">{b.batch_id}</td>
                    <td className="pr-3 font-mono text-xs">{b.first_seq} → {b.last_seq}</td>
                    <td className="pr-3">{b.count}</td>
                    <td className="pr-3">{b.printed_count}{b.expected ? <span className="text-slate-400">/{b.expected}</span> : null}</td>
                    <td className="pr-3"><PrintStatusPill s={b.print_status} /></td>
                    <td className="pr-3">{b.bound_count}</td>
                    <td className="pr-3 text-xs text-slate-500">{(b.created_at || "").slice(0, 10)}</td>
                    <td className="py-2 text-right whitespace-nowrap">
                      {(b.print_status === "partial" || b.print_status === "interrupted") && (
                        <Button data-testid={`recover-batch-${b.batch_id}`} size="sm" className="rounded-lg h-8 mr-1 bg-[#B01E28] hover:bg-[#8f1820] text-white" onClick={() => setRecover(b)}><AlertTriangle className="w-3.5 h-3.5 mr-1" />Recover {b.not_printed_count}</Button>
                      )}
                      <Button data-testid={`print-batch-${b.batch_id}`} size="sm" variant="outline" disabled={!!printingBatch} className="rounded-lg h-8 mr-1" onClick={() => printBatch(b)}>{printingBatch === b.batch_id ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Printing {batchProgress?.done ?? 0}/{batchProgress?.total ?? b.count}…</> : <><Printer className="w-3.5 h-3.5 mr-1" />ZPL</>}</Button>
                      <Button data-testid={`pdf-batch-${b.batch_id}`} size="sm" variant="outline" disabled={!!printingBatch} className="rounded-lg h-8" onClick={() => { const warn = b.printed_count > 0 ? `⚠️ ${b.printed_count} already printed — a re-print makes duplicate labels for the same numbers.\n\n` : ""; if (window.confirm(`${warn}Open a PDF with ${b.count} tag${b.count === 1 ? "" : "s"} as ${mediaLabel} for print run ${b.batch_id}? Print it at Actual Size / 100%.`)) openPdf(`/tags/batch/${b.batch_id}/pdf?media=${media}`); }}><FileText className="w-3.5 h-3.5 mr-1" />PDF</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {batchMeta?.has_more && (
          <Button data-testid="runs-load-more" variant="outline" onClick={() => refresh((batchMeta.page || 1) + 1)}
            className="mt-3 w-full rounded-lg h-10 border-slate-200 text-slate-600">
            Show more — {batchMeta.total - batchMeta.shown} still to come
          </Button>
        )}

        {/* THE PRINTED PILE, ON ONE LINE. His layout: done runs are history, not a wall. */}
        {!runQ.trim() && (batchMeta?.counts?.printed ?? 0) > 0 && (
          <div className="mt-4 border-t border-slate-100 pt-3">
            <button type="button" data-testid="runs-printed-toggle"
              onClick={() => { setShowDone((v) => !v); if (!doneRuns) loadDone(1); }}
              className="w-full flex items-center gap-2 text-left text-sm text-slate-600 hover:text-slate-900">
              {showDone ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
              <span className="font-semibold">Printed runs</span>
              <span className="font-mono text-[11px] text-slate-500">{batchMeta.counts.printed}</span>
              <span className="text-[11px] text-slate-400">— all paper accounted for</span>
            </button>
            {showDone && (
              <div className="overflow-x-auto mt-2" data-testid="runs-printed-list">
                <table className="w-full text-sm">
                  <thead><tr className="text-left font-mono text-[10px] uppercase text-slate-400 border-b border-slate-200">
                    <th className="py-2 pr-3">Print run</th><th className="pr-3">Range</th><th className="pr-3">Count</th><th className="pr-3">Bound</th><th className="pr-3">Created</th><th className="text-right">Print</th>
                  </tr></thead>
                  <tbody>
                    {(doneRuns || []).map((b) => (
                      <tr key={b.batch_id} data-testid={`printed-run-${b.batch_id}`} className="border-b border-slate-100">
                        <td className="py-2 pr-3 font-mono">{b.batch_id}</td>
                        <td className="pr-3 font-mono text-xs">{b.first_seq} → {b.last_seq}</td>
                        <td className="pr-3">{b.count}</td>
                        <td className="pr-3">{b.bound_count}</td>
                        <td className="pr-3 text-xs text-slate-500">{(b.created_at || "").slice(0, 10)}</td>
                        <td className="py-2 text-right whitespace-nowrap">
                          <Button data-testid={`pdf-printed-${b.batch_id}`} size="sm" variant="outline" className="rounded-lg h-8"
                            onClick={() => { if (window.confirm(`⚠️ ${b.printed_count} already printed — a re-print makes duplicate labels for the same numbers.\n\nOpen the PDF for ${b.batch_id} anyway?`)) openPdf(`/tags/batch/${b.batch_id}/pdf?media=${media}`); }}>
                            <FileText className="w-3.5 h-3.5 mr-1" />PDF
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {doneMeta?.has_more && (
                  <Button data-testid="runs-printed-more" variant="outline" onClick={() => loadDone((doneMeta.page || 1) + 1)}
                    className="mt-2 w-full rounded-lg h-10 border-slate-200 text-slate-600">
                    Show more — {doneMeta.total - doneMeta.shown} still to come
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Print a range / recover */}
      <div className="border border-slate-200 bg-white p-4 mb-6" data-testid="range-print-card">
        <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-3">Print a range of tags</div>
        <p className="text-[11px] text-slate-400 mb-3">Reprint a span of FAS numbers as {mediaLabel} — e.g. to recover a run that dropped, or replace lost/damaged labels. Safe default only prints numbers not yet printed on this media.</p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500">From</label>
            <ScanTextInput testid="range-from" placeholder="FAS-000031" value={rangeFrom}
              onChange={(v) => { setRangeFrom(v); setRangePreview(null); }} className="mt-1 w-48" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500">To</label>
            <ScanTextInput testid="range-to" placeholder="FAS-000200" value={rangeTo}
              onChange={(v) => { setRangeTo(v); setRangePreview(null); }} className="mt-1 w-48" />
          </div>
          <Button data-testid="range-preview-button" variant="outline" className="rounded-lg" disabled={rangeBusy} onClick={previewRange}>{rangeBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Tags className="w-4 h-4 mr-1" />}Preview</Button>
        </div>
        {rangePreview && (
          <div className="mt-4 border-t border-slate-100 pt-3" data-testid="range-preview">
            <div className="text-sm text-slate-700">
              Range covers <b>{rangePreview.summary.total_in_range}</b> number(s): <b className="text-emerald-700">{rangePreview.summary.new} new</b>
              {rangePreview.summary.already_this_media > 0 && <> · <b className="text-amber-700">{rangePreview.summary.already_this_media} already printed on {mediaLabel}</b></>}
              {rangePreview.summary.bound > 0 && <> · <b className="text-blue-700">{rangePreview.summary.bound} bound to a part</b></>}
              {rangePreview.summary.voided > 0 && <> · {rangePreview.summary.voided} voided</>}
              {rangePreview.summary.gaps.length > 0 && <> · {rangePreview.summary.gaps.length} gap(s)</>}
            </div>
            {rangePreview.summary.bound > 0 && (
              <div data-testid="range-bound-warning" className="mt-2 text-[11px] text-blue-800 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 flex items-start gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>Bound tags in range: {rangePreview.items.filter((i) => i.bound).map((i) => i.seq_id).join(", ")}. Reprinting a bound tag makes another copy of a label already on a part — only do this to replace a damaged label.</span>
              </div>
            )}
            <div className="flex flex-wrap gap-2 mt-3">
              <Button data-testid="range-print-safe" className="rounded-lg bg-[#E0781A] text-white hover:bg-[#C76714]" disabled={rangePreview.summary.new === 0} onClick={() => printRange(true)}>
                <Printer className="w-4 h-4 mr-1" />Print {rangePreview.summary.new} not-yet-printed
              </Button>
              <Button data-testid="range-print-all" variant="outline" className="rounded-lg border-amber-300 text-amber-800" onClick={() => printRange(false)}>
                Print all in range (incl. reprints)
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Recovery dialog — shows the ACTUAL numbers that didn't print + when the run dropped */}
      <Dialog open={!!recover} onOpenChange={(v) => !v && setRecover(null)}>
        <DialogContent className="rounded-lg border-slate-200 sm:max-w-md" data-testid="recover-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-[#B01E28]" />Recover print run {recover?.batch_id}</DialogTitle></DialogHeader>
          {recover && (
            <>
              <p className="text-sm text-slate-600">
                This run is <b>{recover.print_status === "interrupted" ? "interrupted — completion unknown" : "partial"}</b>.
                {recover.print_started_at && <> Run started <b>{new Date(recover.print_started_at).toLocaleString()}</b>.</>}
              </p>
              <p className="text-xs text-slate-500">These <b>{recover.not_printed_count}</b> number(s) did NOT print — cross-check against your physical stack before reprinting:</p>
              <div data-testid="recover-seq-list" className="font-mono text-xs bg-slate-50 border border-slate-200 rounded-lg p-3 max-h-40 overflow-y-auto break-words">
                {(recover.not_printed_seqs || []).join(", ")}
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-lg border-slate-200" onClick={() => setRecover(null)}>Cancel</Button>
                <Button data-testid="recover-confirm" className="rounded-lg bg-[#B01E28] hover:bg-[#8f1820] text-white" onClick={() => doRecover(recover)}><Printer className="w-4 h-4 mr-1" />Reprint these {recover.not_printed_count} as {mediaLabel}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <ReprintReasonPicker open={!!reasonPicker} onOpenChange={(v) => !v && setReasonPicker(null)}
        count={reasonPicker?.count} currentMedia={media}
        onConfirm={(reason, text, printMedia) => reasonPicker?.onPick(reason, text, printMedia)} />

      {/* Tag inventory */}
      <div className="border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Tag inventory ({tagTotal})</div>
          <div className="flex items-center gap-2">
            <ScanTextInput testid="tag-search" placeholder="Find e.g. 4217" value={q} onChange={setQ}
              className="w-52" inputClassName="h-8 text-sm" />
            <select data-testid="tag-status-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-lg border border-slate-200 h-8 text-sm px-2 bg-white">
              {STATUS_FILTERS.map((s) => <option key={s} value={s}>{s || "all statuses"}</option>)}
            </select>
          </div>
        </div>
        {tags.length === 0 ? <p className="text-sm text-slate-400">No tags match.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="tag-table">
              <thead><tr className="text-left font-mono text-[10px] uppercase text-slate-400 border-b border-slate-200">
                <th className="py-2 pr-3">Code</th><th className="pr-3">Status</th><th className="pr-3">Print run</th><th className="pr-3">Bound</th><th className="text-right">Actions</th>
              </tr></thead>
              <tbody>
                {tags.map((t) => (
                  <tr key={t.id} data-testid={`tag-row-${t.seq_id}`} className="border-b border-slate-100">
                    <td className="py-2 pr-3 font-mono font-bold">{t.seq_id}</td>
                    <td className="pr-3"><StatusPill s={t.status} />{t.printed_at && t.printed_source !== "confirm" && <span data-testid={`tag-unverified-${t.seq_id}`} title="Legacy stamp — a print was recorded before confirm tracking existed. Recorded, not vouched for." className="ml-1 inline-block align-middle rounded bg-slate-100 text-slate-500 border border-slate-200 text-[9px] font-mono px-1">Printed (legacy)</span>}</td>
                    <td className="pr-3 font-mono text-xs text-slate-500">{t.batch_id}</td>
                    <td className="pr-3">{t.bound_part_id ? <Link2 className="w-4 h-4 text-green-600" /> : <span className="text-slate-200">—</span>}</td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <Button data-testid={`tag-print-${t.seq_id}`} size="sm" variant="outline" className="rounded-lg h-8 mr-1" onClick={() => printOne(t)}><Printer className="w-3.5 h-3.5" /></Button>
                      <Button data-testid={`tag-pdf-${t.seq_id}`} size="sm" variant="outline" className="rounded-lg h-8 mr-1" onClick={() => openPdf(`/tags/${t.id}/pdf?media=${media}`)}><FileText className="w-3.5 h-3.5" /></Button>
                      {!t.bound_part_id && t.status !== "voided" && (
                        <Button data-testid={`tag-void-${t.seq_id}`} size="sm" variant="outline" className="rounded-lg h-8 mr-1 text-red-600" onClick={() => lifecycle(t.id, "void")}><Ban className="w-3.5 h-3.5" /></Button>
                      )}
                      {t.status !== "retired" && (
                        <Button data-testid={`tag-retire-${t.seq_id}`} size="sm" variant="outline" className="rounded-lg h-8 text-slate-500" onClick={() => lifecycle(t.id, "retire")}><Archive className="w-3.5 h-3.5" /></Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
